import React, { useMemo } from 'react';

import { useIntl } from 'react-intl-next';
import { usePathParam, useRouter } from 'react-resource-router';

import Button from '@atlaskit/button';
import AutomationIcon from '@atlaskit/icon/core/automation';
import BriefcaseIcon from '@atlaskit/icon/core/briefcase';
import HeartIcon from '@atlaskit/icon/core/heart';
import AddonIcon from '@atlaskit/icon/core/migration/app--addon';
import GraphBarIcon from '@atlaskit/icon/core/migration/chart-bar--graph-bar';
import FeedbackIcon from '@atlaskit/icon/core/migration/feedback';
import PeopleIcon from '@atlaskit/icon/core/migration/people-group--people';
import RefreshIcon from '@atlaskit/icon/core/migration/refresh';
import StopwatchIcon from '@atlaskit/icon/core/migration/stopwatch';
import NotificationIcon from '@atlaskit/icon/core/notification';
import OnCallIcon from '@atlaskit/icon/core/on-call';
import AlertIcon from '@atlaskit/legacy-custom-icons/alert-icon';
import CustomAutomationsIcon from '@atlaskit/legacy-custom-icons/automations-icon';
import HeartbeatsIcon from '@atlaskit/legacy-custom-icons/heartbeats-icon';
import CustomOnCallIcon from '@atlaskit/legacy-custom-icons/on-call-icon';
import PoliciesIcon from '@atlaskit/legacy-custom-icons/policies-icon';
import { SpotlightTarget } from '@atlaskit/onboarding';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import {
	CustomItem,
	Footer,
	LoadingItems,
	NavigationFooter,
	NavigationHeader,
	NestableNavigationContent,
	NestingItem,
	Section,
	SideNavigation,
	SkeletonItem,
} from '@atlaskit/side-navigation';
import { SavedSearches } from '@atlassian/eoc-alert-list/saved-searches';
import { isServer } from '@atlassian/eoc-common';
import { Sidebar as ReportsSidebar } from '@atlassian/eoc-ops-reports-common';
import { useResource } from '@atlassian/eoc-rrr-helpers';

import { SPOTLIGHT_NAMES } from '../../common/constants';
import { Link } from '../../common/ui/link';
import { sidebarRightsResource } from '../../services/resources';

import { Header } from './header';
import messages from './messages';
import { SectionContainer } from './styled';
import { type SidebarProps } from './types';

export const Sidebar = ({ onFeedbackButtonClick }: SidebarProps) => {
	const [{ location }] = useRouter();
	const [teamId] = usePathParam('teamId');
	const { formatMessage } = useIntl();
	const { data: sidebarRights, loading } = useResource(sidebarRightsResource);

	const routeItems = useMemo(
		() => ({
			alerts: `/jira/ops/teams/${teamId}/alerts?query=responders: ${teamId}`,
			onCall: `/jira/ops/teams/${teamId}/on-call`,
			reports: `/jira/ops/teams/${teamId}/reports`,
			integrations: `/jira/ops/teams/${teamId}/integrations`,
			policies: `/jira/ops/teams/${teamId}/policies`,
			automations: `/jira/ops/teams/${teamId}/automations`,
			alertIssueSyncing: `/jira/ops/teams/${teamId}/syncs`,
			settings: `/jira/ops/teams/${teamId}/settings`,
			heartbeats: `/jira/ops/teams/${teamId}/heartbeats`,
			maintenance: `/jira/ops/teams/${teamId}/maintenance`,
		}),
		[teamId],
	);

	const initialStackId = useMemo(() => {
		const nestedRoutes = ['alerts'];
		if (sidebarRights?.showReports) {
			nestedRoutes.push('reports');
		}

		const currentRoute = Object.entries(routeItems).find(([key, value]) =>
			location.pathname.includes(value.split('?')[0]),
		);
		if (currentRoute) {
			const [currentRouteId] = currentRoute;

			if (nestedRoutes.includes(currentRouteId)) {
				return [currentRouteId];
			}
		}

		return [];
	}, [location.pathname, routeItems, sidebarRights?.showReports]);

	return (
		<>
			<UFOLoadHold name="eoc-team-dashboard-sidebar-rights-loading" hold={loading} />
			<SideNavigation label={formatMessage(messages.sidebarLabel)}>
				<NavigationHeader>
					<Header />
				</NavigationHeader>
				<NestableNavigationContent initialStack={initialStackId}>
					<LoadingItems
						isLoading={isServer() || loading}
						fallback={
							<>
								<SkeletonItem hasIcon />
								<SkeletonItem hasIcon />
								<SkeletonItem hasIcon />
								<SkeletonItem hasIcon />
								<SkeletonItem hasIcon />
							</>
						}
					>
						<SpotlightTarget name={SPOTLIGHT_NAMES.sidebarItems}>
							<SectionContainer>
								<Section hasSeparator>
									<NestingItem
										analyticsItemId="eocAlerts"
										component={Link}
										iconBefore={
											<NotificationIcon
												color="currentColor"
												LEGACY_fallbackIcon={AlertIcon}
												label={''}
											/>
										}
										to={routeItems['alerts']}
										id="alerts"
										title={formatMessage(messages.alerts)}
										isSelected={location.pathname.includes(routeItems['alerts'])}
									>
										<SavedSearches />
									</NestingItem>
									<CustomItem
										analyticsItemId="eocOnCall"
										component={Link}
										iconBefore={
											<OnCallIcon
												label=""
												LEGACY_fallbackIcon={CustomOnCallIcon}
												color="currentColor"
											/>
										}
										to={routeItems['onCall']}
										id="onCall"
										isSelected={location.pathname.includes(routeItems['onCall'])}
									>
										{formatMessage(messages.onCall)}
									</CustomItem>
								</Section>
								<Section hasSeparator>
									<CustomItem
										analyticsItemId="eocIntegrations"
										component={Link}
										iconBefore={<AddonIcon color="currentColor" spacing="spacious" label="" />}
										to={routeItems['integrations']}
										id="integrations"
										isSelected={location.pathname.includes(routeItems['integrations'])}
									>
										{formatMessage(messages.integrations)}
									</CustomItem>

									{sidebarRights?.showAutomation && (
										<CustomItem
											analyticsItemId="eocAutomations"
											component={Link}
											iconBefore={
												<AutomationIcon
													LEGACY_fallbackIcon={CustomAutomationsIcon}
													label=""
													color="currentColor"
												/>
											}
											to={routeItems['automations']}
											id="automation"
											isSelected={location.pathname.includes(routeItems['automations'])}
										>
											{formatMessage(messages.automations)}
										</CustomItem>
									)}
									{sidebarRights?.showSync && (
										<CustomItem
											analyticsItemId="eocAlertIssueSyncing"
											component={Link}
											iconBefore={<RefreshIcon color="currentColor" spacing="spacious" label="" />}
											to={routeItems['alertIssueSyncing']}
											id="alertIssueSyncing"
											isSelected={location.pathname.includes(routeItems['alertIssueSyncing'])}
										>
											{formatMessage(messages.alertIssueSyncing)}
										</CustomItem>
									)}
									{sidebarRights?.showHeartbeats && (
										<CustomItem
											analyticsItemId="eocHeartbeats"
											component={Link}
											iconBefore={
												<HeartIcon
													LEGACY_fallbackIcon={HeartbeatsIcon}
													label=""
													color="currentColor"
												/>
											}
											to={routeItems['heartbeats']}
											id="heartbeats"
											isSelected={location.pathname.includes(routeItems['heartbeats'])}
										>
											{formatMessage(messages.heartbeats)}
										</CustomItem>
									)}
								</Section>
								<Section hasSeparator>
									<CustomItem
										analyticsItemId="eocMaintenance"
										component={Link}
										iconBefore={<StopwatchIcon color="currentColor" spacing="spacious" label="" />}
										to={routeItems['maintenance']}
										id="maintenance"
										isSelected={location.pathname.includes(routeItems['maintenance'])}
									>
										{formatMessage(messages.maintenance)}
									</CustomItem>
									{sidebarRights?.showPolicies && (
										<CustomItem
											analyticsItemId="eocPolicies"
											component={Link}
											iconBefore={
												<BriefcaseIcon
													LEGACY_fallbackIcon={PoliciesIcon}
													label=""
													color="currentColor"
												/>
											}
											to={routeItems['policies']}
											id="policies"
											isSelected={location.pathname.includes(routeItems['policies'])}
										>
											{formatMessage(messages.policies)}
										</CustomItem>
									)}
								</Section>
								{sidebarRights?.showReports && (
									<Section hasSeparator={sidebarRights?.showMembers}>
										<NestingItem
											analyticsItemId="eocReports"
											component={Link}
											iconBefore={<GraphBarIcon color="currentColor" spacing="spacious" label="" />}
											to={routeItems['reports']}
											id="reports"
											title={formatMessage(messages.reports)}
											isSelected={location.pathname.includes(routeItems['reports'])}
										>
											<ReportsSidebar baseRoute={`/jira/ops/teams/${teamId}/reports`} />
										</NestingItem>
									</Section>
								)}
								{sidebarRights?.showMembers && (
									<Section hasSeparator>
										<CustomItem
											analyticsItemId="eocSettings"
											component={Link}
											iconBefore={
												<PeopleIcon
													color="currentColor"
													spacing="spacious"
													label={formatMessage(messages.settings)}
												/>
											}
											to={routeItems['settings']}
											id="settings"
											isSelected={location.pathname.includes(routeItems['settings'])}
										>
											{formatMessage(messages.settings)}
										</CustomItem>
									</Section>
								)}
							</SectionContainer>
						</SpotlightTarget>
					</LoadingItems>
				</NestableNavigationContent>
				<NavigationFooter>
					<Footer>
						<Button
							appearance="subtle"
							iconBefore={<FeedbackIcon color="currentColor" label="" LEGACY_size="small" />}
							onClick={onFeedbackButtonClick}
						>
							{formatMessage(messages.giveFeedbackButton)}
						</Button>
					</Footer>
				</NavigationFooter>
			</SideNavigation>
		</>
	);
};
